import { useState } from 'react'

import { useAppToast } from '../../helpers'

const BRASIL_API_CEP_URL = 'https://brasilapi.com.br/api/cep/v1'

export interface IZipCodeAddress {
  street: string
  neighborhood: string
  city: string
  state: string
}

const EMPTY_ADDRESS: IZipCodeAddress = { street: '', neighborhood: '', city: '', state: '' }

interface IOptions {
  /** Chamado com o endereço encontrado, ou com os campos vazios quando o CEP não existe. */
  onAddressFound: (address: IZipCodeAddress) => void
  /** Chamado depois, se o Google Maps estiver carregado e souber geocodificar o endereço. */
  onCoordinatesFound?: (latitude: number, longitude: number) => void
}

/**
 * Busca de endereço por CEP na BrasilAPI, com a geocodificação do resultado.
 *
 * Existiam duas cópias quase idênticas deste fluxo — `Events/EventFormModal` e
 * `Events/series/LocationFields` —, cada uma com o seu `fetch`, o seu `loadingZip` e o seu
 * `geocoder`. **E elas divergiam num ponto que é bug:** a do `EventFormModal` limpava os campos
 * quando a resposta não era `ok` mas não voltava, então seguia lendo o corpo de erro como se fosse
 * endereço e mandava geocodificar `", - , Brasil"`.
 *
 * O aviso de "CEP não encontrado" é do hook: das duas cópias, só uma dava esse retorno — na outra
 * os campos de endereço simplesmente se esvaziavam sem explicação.
 *
 * O `lookup` só dispara com os 8 dígitos, e é seguro chamá-lo a cada tecla.
 */
export function useZipCodeLookup({ onAddressFound, onCoordinatesFound }: IOptions) {
  const [loadingZipCode, setLoadingZipCode] = useState(false)
  const toast = useAppToast()

  const geocode = (address: IZipCodeAddress) => {
    if (!onCoordinatesFound || !window.google?.maps) return

    const geocoder = new google.maps.Geocoder()
    const fullAddress = `${address.street}, ${address.city} - ${address.state}, Brasil`

    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) return

      const location = results[0].geometry.location
      onCoordinatesFound(location.lat(), location.lng())
    })
  }

  const lookup = async (zipCode: string) => {
    const cleaned = zipCode.replace(/\D/g, '')
    if (cleaned.length !== 8) return

    setLoadingZipCode(true)
    try {
      const response = await fetch(`${BRASIL_API_CEP_URL}/${cleaned}`)
      if (!response.ok) {
        onAddressFound(EMPTY_ADDRESS)
        toast.warning('CEP não encontrado')
        return
      }

      const data = await response.json()
      const address: IZipCodeAddress = {
        street: data.street ?? '',
        neighborhood: data.neighborhood ?? '',
        city: data.city ?? '',
        state: data.state ?? ''
      }

      onAddressFound(address)
      geocode(address)
    } catch {
      onAddressFound(EMPTY_ADDRESS)
      toast.warning('CEP não encontrado')
    } finally {
      setLoadingZipCode(false)
    }
  }

  return { loadingZipCode, lookup }
}
